import React, { useState, useEffect } from 'react'
import { motion, useMotionValue, animate, PanInfo } from 'framer-motion'
import { ChevronLeft, BookOpen, Zap } from 'lucide-react'
import { extractLensConstructs, parseSurfaceDiff, type LensConstruct } from '../utils/lensParser'
import InsightDots from './InsightDots'
import type { ShareDetails } from '../services/shareService'
import { highlightCode } from '../utils/syntaxHighlighter'

interface MobileShareSlideshowProps {
  // Option 1: Pass shareData object (for shared links)
  shareData?: ShareDetails
  // Option 2: Pass direct props (for embedded demos)
  sourceLanguage?: string
  targetLanguage?: string
  htmlContent?: string
}

/**
 * Pinterest-style immersive mobile slideshow for shared SyntaxLens insights
 * - Auto-enters on mobile share links
 * - Fullscreen, gesture-only navigation
 * - Minimal chrome, maximum focus on code & insights
 * - Flexible: accepts shareData OR direct props
 */
const MobileShareSlideshow: React.FC<MobileShareSlideshowProps> = ({
  shareData,
  sourceLanguage: propSourceLanguage,
  targetLanguage: propTargetLanguage,
  htmlContent: propHtmlContent
}) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const sx = useMotionValue(0) // Horizontal slider position (pixels only)

  // Extract values from either shareData or direct props
  const htmlContent = shareData?.htmlContent || propHtmlContent || ''
  const sourceLanguage = shareData?.sourceLanguage || propSourceLanguage || ''
  const targetLanguage = shareData?.targetLanguage || propTargetLanguage || ''

  // Extract constructs from HTML
  const constructs = extractLensConstructs(htmlContent)

  const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 375
  const isFirst = currentIndex === 0
  const isLast = currentIndex === constructs.length - 1

  // Snap to slide based on velocity and position
  const handleDragEnd = async (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const { offset, velocity } = info

    // Velocity threshold for quick swipes (px/s)
    const velocityThreshold = 500
    // Distance threshold (25% of screen width)
    const distanceThreshold = screenWidth * 0.25

    let newIndex = currentIndex

    // Determine direction based on velocity OR distance
    if (Math.abs(velocity.x) > velocityThreshold) {
      // Fast swipe - use velocity direction
      if (velocity.x > 0 && !isFirst) {
        newIndex = currentIndex - 1 // Swipe right (prev)
      } else if (velocity.x < 0 && !isLast) {
        newIndex = currentIndex + 1 // Swipe left (next)
      }
    } else if (Math.abs(offset.x) > distanceThreshold) {
      // Slow drag - use distance
      if (offset.x > 0 && !isFirst) {
        newIndex = currentIndex - 1
      } else if (offset.x < 0 && !isLast) {
        newIndex = currentIndex + 1
      }
    }

    // Update index and animate to position
    setCurrentIndex(newIndex)

    // Spring animation to snap position (use MotionValue directly)
    await animate(sx, -newIndex * screenWidth, {
      type: 'spring',
      stiffness: 300,
      damping: 30,
      mass: 0.8
    })
  }

  // Update position when index changes (e.g., keyboard navigation)
  useEffect(() => {
    animate(sx, -currentIndex * screenWidth, {
      type: 'spring',
      stiffness: 300,
      damping: 30,
      mass: 0.8
    })
  }, [currentIndex, screenWidth, sx])

  const nextSlide = () => {
    if (!isLast) {
      setCurrentIndex(prev => prev + 1)
    }
  }

  const prevSlide = () => {
    if (!isFirst) {
      setCurrentIndex(prev => prev - 1)
    }
  }

  // Keyboard navigation for testing on desktop
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault()
          nextSlide()
          break
        case 'ArrowLeft':
          e.preventDefault()
          prevSlide()
          break
        case 'Escape':
          e.preventDefault()
          handleExit()
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentIndex, constructs.length])

  // Prevent body scroll when slideshow is active
  useEffect(() => {
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [])

  const handleExit = () => {
    // Check if there's a return URL (e.g., from SharesAdmin)
    const returnUrl = localStorage.getItem('syntaxlens_return_url')

    if (returnUrl) {
      // Clear it and navigate back
      localStorage.removeItem('syntaxlens_return_url')
      window.location.href = returnUrl
    } else {
      // Default: navigate to home
      window.location.href = '/'
    }
  }

  if (constructs.length === 0) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center text-white px-6">
          <p className="text-lg mb-4">No insights available</p>
          <button
            onClick={handleExit}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
      {/* Minimal Top Bar - Back Button + Dots */}
      <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between px-4 py-4">
        {/* Back Button (top-left) */}
        <button
          onClick={handleExit}
          className="p-2 -ml-2 hover:bg-white/10 active:bg-white/20 rounded-full transition-colors"
          aria-label="Exit slideshow"
          style={{ minWidth: '40px', minHeight: '40px' }}
        >
          <ChevronLeft className="w-6 h-6 text-white" strokeWidth={2.5} />
        </button>

        {/* Progress Dots (top-center) */}
        <div className="absolute left-1/2 -translate-x-1/2">
          <InsightDots total={constructs.length} currentIndex={currentIndex} />
        </div>

        {/* Spacer for symmetry */}
        <div className="w-10" />
      </div>

      {/* Slides Container - Framer Motion Drag */}
      <div className="h-full pt-16 pb-8 overflow-hidden">
        <motion.div
          className="h-full flex"
          style={{ x: sx }}
          drag="x"
          dragConstraints={{
            left: -screenWidth * (constructs.length - 1),
            right: 0
          }}
          dragElastic={0.2}
          dragDirectionLock
          dragPropagation={false}
          onDragEnd={handleDragEnd}
        >
          {/* Render all slides for smooth drag */}
          {constructs.map((construct, index) => (
            <SlideContent
              key={index}
              construct={construct}
              sourceLanguage={sourceLanguage}
              targetLanguage={targetLanguage}
              isActive={index === currentIndex}
              showHint={index === 0 && currentIndex === 0}
            />
          ))}
        </motion.div>
      </div>
      {/* Custom styles for animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes slide-in-from-right-4 {
            from {
              opacity: 0;
              transform: translateX(16px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

          @keyframes slide-in-from-bottom-2 {
            from {
              opacity: 0;
              transform: translateY(8px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .animate-in {
            animation-fill-mode: both;
          }

          .fade-in {
            animation-name: fade-in;
          }

          @keyframes fade-in {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          .fade-out {
            animation-name: fade-out;
          }

          @keyframes fade-out {
            from {
              opacity: 1;
            }
            to {
              opacity: 0;
              pointer-events: none;
            }
          }

          .slide-in-from-right-4 {
            animation-name: slide-in-from-right-4;
          }

          .slide-in-from-bottom-2 {
            animation-name: slide-in-from-bottom-2;
          }

          .duration-300 {
            animation-duration: 300ms;
          }

          .duration-500 {
            animation-duration: 500ms;
          }

          .delay-500 {
            animation-delay: 500ms;
          }

          .delay-3000 {
            animation-delay: 3000ms;
          }
        `
      }} />
    </div>
  )
}

// Individual Slide Content Component
interface SlideContentProps {
  construct: LensConstruct
  sourceLanguage: string
  targetLanguage: string
  isActive: boolean
  showHint: boolean
}

const SlideContent: React.FC<SlideContentProps> = ({
  construct,
  sourceLanguage,
  targetLanguage,
  isActive,
  showHint
}) => {
  const { source, target } = parseSurfaceDiff(construct.surface)
  const [isParadigmRevealed, setIsParadigmRevealed] = useState(false)
  const paradigmRef = React.useRef<HTMLDivElement>(null)

  // Paradigm sheet position: pixels only (no calc, no %)
  // Use element's actual height (not screen height!) since it's in a flex-1 container
  const [peekY, setPeekY] = useState(755) // Default fallback
  const py = useMotionValue(peekY) // Paradigm y position

  // Measure actual element height on mount to calculate correct peek position
  useEffect(() => {
    if (paradigmRef.current) {
      const height = paradigmRef.current.offsetHeight
      const calculatedPeekY = height - 45 // Show 45px
      setPeekY(calculatedPeekY)
      py.set(calculatedPeekY) // Update MotionValue with correct position
    }
  }, [py])

  // Format explanation text with markdown-to-HTML conversion
  const formatExplanationText = (text: string): string => {
    if (!text) return ''

    let formatted = text

    // Escape HTML first to prevent XSS
    formatted = formatted.replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')

    // Convert markdown to HTML (order matters!)

    // 1. Inline code (backticks) - do first to protect code content
    formatted = formatted.replace(/`([^`]+)`/g, (_, code) => {
      return `<code style="font-family: ui-monospace, SFMono-Regular, 'JetBrains Mono', Menlo, Monaco, Consolas, monospace; font-size: 14px; background-color: rgba(255, 255, 255, 0.15); padding: 2px 6px; border-radius: 4px; font-weight: 500; letter-spacing: -0.01em;">${code}</code>`
    })

    // 2. Bold (**text** or __text__)
    formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong style="font-weight: 600; color: rgba(255, 255, 255, 0.95);">$1</strong>')
    formatted = formatted.replace(/__([^_]+)__/g, '<strong style="font-weight: 600; color: rgba(255, 255, 255, 0.95);">$1</strong>')

    // 3. Italic (*text* or _text_) - after bold to avoid conflicts
    formatted = formatted.replace(/\*([^*]+)\*/g, '<em style="font-style: italic;">$1</em>')
    formatted = formatted.replace(/_([^_]+)_/g, '<em style="font-style: italic;">$1</em>')

    // 4. Line breaks (double newline = paragraph, single = br)
    formatted = formatted.replace(/\n\n/g, '</p><p style="margin-top: 0.75rem;">')
    formatted = formatted.replace(/\n/g, '<br />')

    // 5. Wrap in paragraph if not already wrapped
    if (!formatted.startsWith('<p')) {
      formatted = `<p>${formatted}</p>`
    }

    return formatted
  }

  const handleParadigmDragEnd = async (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const threshold = 80 // pixels to trigger reveal (negative for upward swipe)

    // Check if drag was primarily vertical (prevent horizontal confusion)
    const isVerticalDrag = Math.abs(info.offset.y) > Math.abs(info.offset.x) * 2

    if (isVerticalDrag && info.offset.y < -threshold) {
      // Reveal paradigm (swiped up from peek)
      setIsParadigmRevealed(true)
      await animate(py, 0, {
        type: 'spring',
        stiffness: 300,
        damping: 30,
        mass: 0.8
      })
    } else {
      // Snap back to peek position
      await animate(py, peekY, {
        type: 'spring',
        stiffness: 300,
        damping: 30
      })
    }
  }

  // Reset paradigm state when slide changes - IMMEDIATE, no race conditions
  useEffect(() => {
    if (isActive) {
      // Slide became active - reset immediately to peek state
      setIsParadigmRevealed(false)
      py.set(peekY)
    }
  }, [isActive, py, peekY])

  return (
    <div className="w-screen h-full flex-shrink-0 overflow-hidden flex flex-col">
      {/* Code Block - Top Section */}
      <div className="flex-shrink-0">
        {/* Full-Width Unified Code Block */}
        {(source || target || construct.code) && (
          <div className="w-screen bg-slate-800">
            {/* Source Language (Top) */}
            <div>
              <div className="px-4 pt-3 pb-2 flex items-center justify-end gap-1.5">
                <span className="text-white/40 text-[11px] uppercase tracking-wider">
                  {construct.conceptName}
                </span>
                <span className="text-white/40 text-[11px]">·</span>
                <span className="text-white/40 text-[11px] uppercase tracking-wider font-medium">
                  {sourceLanguage}
                </span>
              </div>
              <div className="px-4 pb-3">
                <pre className="text-[15px] leading-relaxed text-slate-100 font-mono font-medium overflow-x-auto">
                  <code
                    className="block whitespace-pre"
                    dangerouslySetInnerHTML={{
                      __html: highlightCode(source || '(similar)', sourceLanguage)
                    }}
                  />
                </pre>
              </div>
            </div>

            {/* Thin divider */}
            <div className="border-t border-white/5" />

            {/* Target Language (Bottom) */}
            <div>
              <div className="px-4 pt-3 pb-2 flex items-center justify-end">
                <span className="text-white/40 text-[11px] uppercase tracking-wider font-medium">
                  {targetLanguage}
                </span>
              </div>
              <div className="px-4 pb-3">
                <pre className="text-[15px] leading-relaxed text-slate-100 font-mono font-medium overflow-x-auto">
                  <code
                    className="block whitespace-pre"
                    dangerouslySetInnerHTML={{
                      __html: highlightCode(target || construct.code, targetLanguage)
                    }}
                  />
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Section - Semantic Meaning (covers remaining space) */}
      <div className="flex-1 relative overflow-hidden">
        {/* Semantic Meaning - Fixed Background Layer with Swipe Overlay */}
        {construct.concept && (
          <>
            {/* Background Content - Fixed */}
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col" style={{ zIndex: 1 }}>
              <div className="flex-1 overflow-y-auto px-4 pt-10 pb-6" style={{ touchAction: 'pan-y' }}>
                <div className="border-l-2 border-emerald-400/60 pl-4 pr-2 py-3">
                  <h3 className="text-emerald-300/80 font-semibold text-[11px] tracking-wide mb-2 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Semantic meaning
                  </h3>
                  <div
                    className="text-white/90 text-base leading-loose leading-[1.8]"
                    dangerouslySetInnerHTML={{
                      __html: formatExplanationText(construct.concept)
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Swipe-Up Capture Layer - Invisible, captures gestures over entire area */}
            {construct.paradigm && !isParadigmRevealed && (
              <motion.div
                drag="y"
                dragConstraints={{ top: -400, bottom: 0 }}
                dragElastic={0.1}
                dragDirectionLock
                dragMomentum={false}
                dragTransition={{ bounceStiffness: 300, bounceDamping: 30 }}
                onDrag={(_event, info) => {
                  // Only show preview for upward drags from peek position
                  if (info.offset.y < 0) {
                    const dragAmount = Math.abs(info.offset.y)
                    const revealDistance = peekY // Distance from peek to full open
                    const progress = Math.min(dragAmount / revealDistance, 1)

                    // Calculate position in pixels: starts at peekY, moves to 0
                    const yPosition = peekY * (1 - progress)
                    py.set(yPosition)
                  }
                }}
                onDragEnd={handleParadigmDragEnd}
                className="absolute inset-0"
                style={{
                  zIndex: 10,
                  touchAction: 'none' // Let framer handle all touch
                }}
              />
            )}
          </>
        )}

        {/* Paradigm Shift - Slides Up From Below (iOS Bottom Sheet Style with Edge Peek) */}
        {construct.paradigm && (
          <motion.div
            ref={paradigmRef}
            initial={{ y: peekY }}
            drag={isParadigmRevealed ? "y" : false}
            dragConstraints={{ top: 0, bottom: 300 }}
            dragElastic={0.1}
            dragDirectionLock
            dragMomentum={false}
            onDrag={(_event, info) => {
              // Only respond to primarily vertical drags (prevent interference with horizontal navigation)
              const isVerticalDrag = Math.abs(info.offset.y) > Math.abs(info.offset.x) * 2

              if (!isVerticalDrag && isParadigmRevealed) {
                // Lock paradigm in place during horizontal navigation
                py.set(0)
                return
              }
            }}
            onDragEnd={async (_event, info) => {
              // Swipe down to close - return to peek
              const isVerticalDrag = Math.abs(info.offset.y) > Math.abs(info.offset.x) * 2
              if (isParadigmRevealed && isVerticalDrag && info.offset.y > 100) {
                setIsParadigmRevealed(false)
                await animate(py, peekY, {
                  type: 'spring',
                  stiffness: 300,
                  damping: 30
                })
              } else if (isParadigmRevealed) {
                // Snap back to revealed position
                await animate(py, 0, {
                  type: 'spring',
                  stiffness: 300,
                  damping: 30
                })
              }
            }}
            className="absolute inset-x-0 bottom-0 h-full bg-gradient-to-b from-purple-500/8 via-purple-500/4 to-transparent"
            style={{
              y: py,
              pointerEvents: isParadigmRevealed ? 'auto' : 'none',
              zIndex: 20
            }}
          >
            <div className="h-full flex flex-col">
              {/* Drag handle indicator */}
              <div className="flex-shrink-0 pt-3 pb-2 flex justify-center">
                <div className="w-12 h-1 bg-white/20 rounded-full" />
              </div>

              <div className="flex-1 overflow-y-auto px-4 pb-6">
                <div className="border-l-2 border-purple-400/60 pl-4 pr-2 py-3">
                  <h3 className="text-purple-300/80 font-semibold text-[11px] tracking-wide mb-2 flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    Paradigm shift
                  </h3>
                  <div
                    className="text-white/90 text-base leading-loose leading-[1.8]"
                    dangerouslySetInnerHTML={{
                      __html: formatExplanationText(construct.paradigm)
                    }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>

    </div>
  )
}

export default MobileShareSlideshow
